/**
 * Module dependencies.
 */

import React, { useEffect, useRef, useState } from 'react';
import { EditName } from './edit-name';
import { RemoveItem } from './remove-item';
import { FileText, Folder, Star } from 'lucide-react';
import { cn } from 'app/lib/utils';
import { useActionsContent } from './actions-provider';

/**
 * `TreeNode` type.
 */

export type TreeNode = {
  key: string;
  title: string | React.ReactNode;
  isLeaf?: boolean;
  favorite?: boolean;
  children?: TreeNode[];
};

/**
 * `Props` type.
 */

type Props = {
  node: TreeNode;
};

/**
 * Export `FileButton` component.
 */

export function FileButton({ node }: Props): JSX.Element {
  const { postEditItem, toggleFavorite } = useActionsContent();
  const name = typeof node.title === 'string' ? node.title : '';
  const isLeaf = node.isLeaf ?? true;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming) {
      setDraft(name);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [name, renaming]);

  const commit = () => {
    const next = draft.trim();

    setRenaming(false);

    if (next && next !== name) {
      postEditItem({ id: node.key, name: next });
    }
  };

  return (
    <div className={'flex min-w-0 flex-1 items-center gap-2'}>
      {isLeaf ? (
        <FileText className={'h-4 w-4 shrink-0 text-muted-foreground'} />
      ) : (
        <Folder className={'h-4 w-4 shrink-0 text-muted-foreground'} />
      )}

      {renaming ? (
        <input
          className={'min-w-0 flex-1 rounded border border-input bg-background px-1 py-0.5 text-sm outline-none'}
          onBlur={commit}
          onChange={event => setDraft(event.target.value)}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => {
            event.stopPropagation();

            if (event.key === 'Enter') {
              commit();
            }

            if (event.key === 'Escape') {
              setRenaming(false);
            }
          }}
          ref={inputRef}
          value={draft}
        />
      ) : (
        <span
          className={'min-w-0 flex-1 truncate'}
          onDoubleClick={event => {
            event.stopPropagation();
            setRenaming(true);
          }}
          title={name}
        >
          {name}
        </span>
      )}

      <div className={'flex shrink-0 items-center gap-1'}>
        {isLeaf && (
          <button
            className={cn(
              'rounded p-0.5 transition-opacity hover:text-foreground',
              node.favorite ? 'text-amber-500 opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100'
            )}
            onClick={event => {
              event.stopPropagation();
              toggleFavorite({ id: node.key, favorite: !node.favorite });
            }}
            type={'button'}
          >
            <Star className={cn('h-3.5 w-3.5', node.favorite && 'fill-current')} />
          </button>
        )}

        <div className={'flex items-center gap-1 opacity-0 group-hover:opacity-100'}>
          <RemoveItem node={node} />

          <EditName node={node} />
        </div>
      </div>
    </div>
  );
}
